import React, { CSSProperties, DragEvent, ReactNode, useMemo } from 'react';
import { EventProps, EventSimpleProps } from './Calendar';
import { Classes, Icon, Space } from '../index';
import { formatTimeRange } from './utils';
import classNames from 'classnames';
import {
  getCaledarEventColorScheme,
  getCaledarEventCustomColor,
} from '../common/classes';
import {
  CALENDAR_EVENTSIZE_LIMITS,
  EVENT_VERTICAL_MARGIN,
  EventSize,
  CALENDAR_EVENT_MIN_HEIGHT,
} from './consts';
import { CalendarEventTooltip } from './CalendarEventTooltip';
import { mcCopy } from '@mc/react-ui-icons';
import { Locale } from 'date-fns';

type EventComponentProps<ID> = {
  event: EventSimpleProps<ID>;
  colorIndex?: number;
  /**
   * Šířka sloupce s obsahem pro eventy v pixelech.
   */
  columnContentWidth: number;
  onClick?: () => void;
  /**
   * Pokukd true, bude event zobrazen průhledně, použití když má být vidět na místě odkud se přesouvá.
   */
  originalPosition: boolean;
  /**
   * Pokud true, zobrazuje se bez absolutního pozicovánní.
   */
  inline?: boolean;
  /*
   * Zda se jedná o klonovaný event - renderování eventu který se stane klonem.
   */
  cloning?: boolean;
  locale: Locale;
  /**
   * Zvýraznit? Obdoba jako hover, selected apod.
   */
  active?: boolean;
  /**
   * Zda je event vybrán.
   */
  selected?: boolean;
  pointerEventsNone: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  handleDragStart?: (
    e: DragEvent<HTMLDivElement>,
    event: EventSimpleProps<ID>
  ) => void;
  handleResizeDragStart?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    top: boolean,
    event: EventSimpleProps<ID>
  ) => void;

  /**
   * Vlastní tooltip eventu
   * @param event
   */
  customEventTooltip?: (event?: EventProps<ID>) => ReactNode;
};

export const CalendarEvent = <ID extends unknown = {}>({
  event,
  inline,
  active,
  locale,
  handleDragStart,
  handleResizeDragStart,
  pointerEventsNone,
  onEdit,
  onDelete,
  originalPosition,
  colorIndex = 0,
  selected,
  cloning,
  onClick,
  columnContentWidth,
  customEventTooltip,
}: EventComponentProps<ID>) => {
  const compact = useMemo<EventSize>(() => {
    if (event.height < CALENDAR_EVENTSIZE_LIMITS[EventSize.SMALL]) {
      return EventSize.SMALL;
    } else if (event.height < CALENDAR_EVENTSIZE_LIMITS[EventSize.MEDIUM]) {
      return EventSize.MEDIUM;
    }
    return EventSize.FULL;
  }, [event.height]);

  // TODO konstanta
  const eventsSpacing = 4;

  const positionStyles: CSSProperties = {};
  if (!inline) {
    (positionStyles.position = 'absolute'),
      (positionStyles.top = event.topOffset + EVENT_VERTICAL_MARGIN),
      // TODO XXX
      //   (positionStyles.height = event.height - 6);
      (positionStyles.height =
        event.height - EVENT_VERTICAL_MARGIN - EVENT_VERTICAL_MARGIN);

    // positionStyles.left = `calc((100% / ${event.overlappingStatus?.overlappingDates}) * ${event.overlappingStatus?.overLappedIndex})`;
    positionStyles.left =
      (columnContentWidth / (event.overlappingStatus?.overlappingDates || 1)) *
      (event.overlappingStatus?.overLappedIndex || 0);
    // TODO XXX
    // positionStyles.width = `calc((100% / ${event.overlappingStatus?.overlappingDates}) - 30px)`;
    // positionStyles.width = `calc((100% / ${event.overlappingStatus?.overlappingDates}))`;
    positionStyles.width =
      columnContentWidth / (event.overlappingStatus?.overlappingDates || 1) -
      eventsSpacing;
  }

  if (
    positionStyles &&
    (positionStyles.height as number) < CALENDAR_EVENT_MIN_HEIGHT
  ) {
    positionStyles.height = CALENDAR_EVENT_MIN_HEIGHT;
  }

  return (
    <div
      style={{
        height: '100%',
      }}
      onClick={onClick}
    >
      <CalendarEventTooltip
        locale={locale}
        onEdit={onEdit}
        onDelete={onDelete}
        trigger={
          <div
            onDoubleClick={() => !event?.orignalEvent?.disabled && onEdit?.()}
            key={event.height + event.columnIndex + event.topOffset.toString()}
            draggable={!event?.orignalEvent?.disabled}
            onDragStart={(e) =>
              !event?.orignalEvent?.disabled && handleDragStart?.(e, event)
            }
            className={classNames(
              Classes.CALENDAR_EVENT,
              getCaledarEventColorScheme(colorIndex),
              {
                [Classes.CALENDAR_EVENT_SELECTED]:
                  selected && !originalPosition,
                [Classes.CALENDAR_EVENT_CLONE]: cloning,
                [Classes.CALENDAR_EVENT_ACTIVE]: active,
                [Classes.CALENDAR_EVENT_INLINE]: inline,
                [Classes.CALENDAR_EVENT_ORIGINAL_POSITION]: originalPosition,
                [getCaledarEventCustomColor(event.orignalEvent!.intent!)]:
                  event?.orignalEvent?.intent,
                [Classes.CALEDNAR_EVENT_DISABLED]:
                  event?.orignalEvent?.disabled,
                [Classes.CALEDNAR_EVENT_ENABLED]:
                  !event?.orignalEvent?.disabled,
              }
            )}
            style={{
              ...positionStyles,
              cursor: pointerEventsNone ? 'auto' : 'pointer',
              pointerEvents: pointerEventsNone ? 'none' : 'auto',
            }}
          >
            <div
              className={classNames(Classes.CALENDAR_EVENT_BODY, {
                [Classes.CALDENDAR_EVENT_BODY_SMALL]:
                  compact === EventSize.SMALL,
                [Classes.CALEDNAR_EVENT_BODY_MEDIUM]:
                  compact === EventSize.MEDIUM,
              })}
            >
              {cloning && (
                <div className={Classes.CALENDAR_EVENT_CLONE_SYMBOL}>
                  <Icon fixedWidth icon={mcCopy} />
                </div>
              )}
              {!event?.orignalEvent?.disabled && (
                <div
                  onMouseDown={(e) => handleResizeDragStart?.(e, true, event)}
                  className={Classes.CALENDAR_EVENT_HANDLE_UP}
                />
              )}
              <Space size={'1'} className={'h-100'} vertical>
                {event.title && (
                  <span className={Classes.CALENDAR_EVENT_TITLE}>
                    {/*{event.overlappingStatus?.overlappingDates || 1}/*/}
                    {/*{event.overlappingStatus?.overLappedIndex || 0}/*/}
                    {event.title}
                  </span>
                )}
                {event.orignalEvent && compact !== EventSize.SMALL && (
                  <span className={Classes.CALENDAR_EVENT_TIME}>
                    {formatTimeRange(event.orignalEvent, locale, true)}
                  </span>
                )}
                {event.description && compact === EventSize.FULL && (
                  <span className={Classes.CALENDAR_EVENT_DESCRIPTION}>
                    {event.description}
                  </span>
                )}
              </Space>
              {!event?.orignalEvent?.disabled && (
                <div
                  onMouseDown={(e) => handleResizeDragStart?.(e, false, event)}
                  className={Classes.CALENDAR_EVENT_HANDLE_DOWN}
                />
              )}
            </div>
          </div>
        }
        event={event.orignalEvent}
        customEventTooltip={customEventTooltip}
      />
    </div>
  );
};
